/**
 * FirestoreService V2 - Direct database access layer
 * High-performance service for BackOffice with TypeScript support
 */

import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  DocumentData,
  QueryConstraint,
  Unsubscribe,
  runTransaction,
  Transaction,
  serverTimestamp,
  FieldValue
} from 'firebase/firestore';
import { firebase } from '@core/firebase/app';

export interface QueryFilter {
  field: string;
  operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in' | 'array-contains-any';
  value: any;
}

export interface OrderByClause {
  field: string;
  direction: 'asc' | 'desc';
}

class FirestoreServiceV2 {
  private static instance: FirestoreServiceV2;
  private firestore: Firestore | null = null;
  private listeners: Map<string, Unsubscribe> = new Map();

  private constructor() {
    // Auto-initialize with existing Firebase instance
    this.firestore = firebase.firestore;
  }

  public static getInstance(): FirestoreServiceV2 {
    if (!FirestoreServiceV2.instance) {
      FirestoreServiceV2.instance = new FirestoreServiceV2();
    }
    return FirestoreServiceV2.instance;
  }

  /**
   * Initialize with Firestore instance (optional - auto-initialized with existing Firebase)
   */
  public initialize(firestore?: Firestore): void {
    if (firestore) {
      this.firestore = firestore;
    }
    // Already initialized in constructor with firebase.firestore
  }

  /**
   * Get a single document
   */
  public async getDocument<T extends DocumentData>(
    collectionPath: string,
    documentId: string
  ): Promise<T | null> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const docRef = doc(this.firestore, collectionPath, documentId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as T;
      }
      return null;
    } catch (error) {
      console.error(`Failed to get document ${collectionPath}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Save/update a document
   */
  public async saveDocument<T extends DocumentData>(
    collectionPath: string,
    documentId: string,
    data: Partial<T>,
    merge = true
  ): Promise<void> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const docRef = doc(this.firestore, collectionPath, documentId);
      await setDoc(docRef, data, { merge });
    } catch (error) {
      console.error(`Failed to save document ${collectionPath}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Update specific fields in a document
   */
  public async updateDocument<T extends DocumentData>(
    collectionPath: string,
    documentId: string,
    updates: Partial<T>
  ): Promise<void> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const docRef = doc(this.firestore, collectionPath, documentId);
      await updateDoc(docRef, updates as DocumentData);
    } catch (error) {
      console.error(`Failed to update document ${collectionPath}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Add a new document with auto-generated ID
   */
  public async addDocument<T extends DocumentData>(
    collectionPath: string,
    data: T
  ): Promise<string> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const collectionRef = collection(this.firestore, collectionPath);
      const docRef = await addDoc(collectionRef, data);
      return docRef.id;
    } catch (error) {
      console.error(`Failed to add document to ${collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document
   */
  public async deleteDocument(
    collectionPath: string,
    documentId: string
  ): Promise<void> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const docRef = doc(this.firestore, collectionPath, documentId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Failed to delete document ${collectionPath}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Query documents with filters
   */
  public async queryDocuments<T extends DocumentData>(
    collectionPath: string,
    filters: QueryFilter[] = [],
    orderByClause?: OrderByClause,
    limitCount?: number
  ): Promise<T[]> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      const constraints: QueryConstraint[] = [];

      // Add filters
      filters.forEach(filter => {
        constraints.push(where(filter.field, filter.operator, filter.value));
      });

      // Add ordering
      if (orderByClause) {
        constraints.push(orderBy(orderByClause.field, orderByClause.direction));
      }

      // Add limit
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      const q = query(collection(this.firestore, collectionPath), ...constraints);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as T));
    } catch (error) {
      console.error(`Failed to query documents in ${collectionPath}:`, error);
      throw error;
    }
  }

  /**
   * Subscribe to document changes
   */
  public subscribeToDocument<T extends DocumentData>(
    collectionPath: string,
    documentId: string,
    callback: (data: T | null) => void
  ): Unsubscribe {
    if (!this.firestore) throw new Error('Firestore not initialized');

    const docRef = doc(this.firestore, collectionPath, documentId);

    const unsubscribe = onSnapshot(
      docRef,
      (doc) => {
        if (doc.exists()) {
          callback({ id: doc.id, ...doc.data() } as T);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error(`Subscription error for ${collectionPath}/${documentId}:`, error);
        callback(null);
      }
    );

    // Store listener
    const key = `${collectionPath}/${documentId}`;
    this.listeners.set(key, unsubscribe);

    return unsubscribe;
  }

  /**
   * Subscribe to collection/query changes
   */
  public subscribeToQuery<T extends DocumentData>(
    collectionPath: string,
    filters: QueryFilter[] = [],
    orderByClause?: OrderByClause,
    limitCount?: number,
    callback?: (data: T[]) => void
  ): Unsubscribe {
    if (!this.firestore) throw new Error('Firestore not initialized');

    const constraints: QueryConstraint[] = [];

    // Add filters
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });

    // Add ordering
    if (orderByClause) {
      constraints.push(orderBy(orderByClause.field, orderByClause.direction));
    }

    // Add limit
    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    const q = query(collection(this.firestore, collectionPath), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as T));
        callback?.(data);
      },
      (error) => {
        console.error(`Subscription error for query in ${collectionPath}:`, error);
        callback?.([]);
      }
    );

    return unsubscribe;
  }

  /**
   * Run a transaction
   */
  public async runTransaction<T>(
    updateFunction: (transaction: Transaction) => Promise<T>
  ): Promise<T> {
    if (!this.firestore) throw new Error('Firestore not initialized');

    try {
      return await runTransaction(this.firestore, updateFunction);
    } catch (error) {
      console.error('Transaction failed:', error);
      throw error;
    }
  }

  /**
   * Get server timestamp
   */
  public getServerTimestamp(): FieldValue {
    return serverTimestamp();
  }

  /**
   * Cleanup all listeners
   */
  public cleanup(): void {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners.clear();
  }
}

export const firestoreService = FirestoreServiceV2.getInstance();
export { FirestoreServiceV2 as FirestoreService };
export default firestoreService;